import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  useOpportunities,
  useOpportunityPipelineSummary,
} from "@/hooks/use-api";
import { BoardCard } from "@/components/v2/board-card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { useOpportunityDisplayStore } from "@/stores/opportunity-display-store";
import { useOpportunityViewParams } from "@/components/v2/opportunities/use-opportunity-view-params";
import { useOpportunityFilters } from "@/hooks/use-opportunity-filters";
import { cn, formatCurrency } from "@/lib/utils";

// The opportunity pipeline stages, in board order. (Previously imported from the
// stage-transition-gates module, which was removed along with the kebab
// "Move to" affordance — the board is read-only for now. Drag-and-drop and the
// transition gates return in a later slice.)
const OPPORTUNITY_STAGES = [
  "lead",
  "appointment",
  "proposal",
  "closed_won",
  "closed_lost",
] as const;
type OpportunityStage = (typeof OPPORTUNITY_STAGES)[number];

type Row = Record<string, unknown>;

const COLUMN_LABELS: Record<OpportunityStage, string> = {
  lead: "Lead",
  appointment: "Appointment",
  proposal: "Proposal",
  closed_won: "Closed Won",
  closed_lost: "Closed Lost",
};

// Per-column Type-2 (relationship-not-yet-triggered) teach lines — the column
// trigger IS the teaching surface and doubles as gate documentation (empty-
// states-that-teach; spec surface deliverable 4).
const COLUMN_EMPTY: Record<OpportunityStage, string> = {
  lead: "New opportunities land here.",
  appointment: "Opportunities move here when an appointment is scheduled.",
  proposal: "Opportunities move here when a proposal is sent.",
  closed_won: "Opportunities move here when a customer accepts a proposal.",
  closed_lost: "Opportunities closed as lost are kept here.",
};

// Column stage-tint dot (kanban brief): lead/appointment/proposal = accent,
// closed_won = success, closed_lost = MUTED (faded, not flagged — never
// danger/warning; fixes the shipped warning/danger tints at board.tsx:58,61).
const STAGE_DOT: Record<OpportunityStage, string> = {
  lead: "bg-[color:var(--color-accent)]",
  appointment: "bg-[color:var(--color-accent)]",
  proposal: "bg-[color:var(--color-accent)]",
  closed_won: "bg-[color:var(--color-success)]",
  closed_lost: "bg-[color:var(--color-text-lo)]",
};

/** Recency (§4.3): relative under 30 days, absolute date beyond — never the
 * "8 months ago" relative form that reads as neglect rather than information. */
function formatRecency(dateStr: string | undefined): string | undefined {
  if (!dateStr) return undefined;
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return undefined;
  const diffDays = Math.floor(
    (Date.now() - date.getTime()) / (1000 * 60 * 60 * 24),
  );
  if (diffDays <= 0) return "Today";
  if (diffDays === 1) return "1d";
  if (diffDays < 30) return `${diffDays}d`;
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function asStage(value: unknown): OpportunityStage {
  return (OPPORTUNITY_STAGES as readonly string[]).includes(value as string)
    ? (value as OpportunityStage)
    : "lead";
}

// Within-column ordering (Display popover §3b). The amount field sorts
// numerically; everything else is treated as a date. A stale URL referencing a
// not-yet-shipped field (e.g. stage_entered_at) sorts to a no-op, never throws.
function compareRows(field: string, desc: boolean) {
  return (a: Row, b: Row): number => {
    let cmp: number;
    if (field === "proposal_value") {
      cmp = ((a[field] as number) || 0) - ((b[field] as number) || 0);
    } else {
      const at = a[field] ? new Date(a[field] as string).getTime() || 0 : 0;
      const bt = b[field] ? new Date(b[field] as string).getTime() || 0 : 0;
      cmp = at - bt;
    }
    return desc ? -cmp : cmp;
  };
}

// Columns flex to fill the rail; they hold a 17rem floor so cards have room to
// breathe and titles wrap less (Ryan review) — on wide screens the columns
// grow to fill, on narrower ones the rail scrolls (overscroll-contained) rather
// than crushing the cards. The filled header row carries the column identity
// (--color-surface-raised was never added to @adva/tokens); the body stays open
// over the page bg.
const COLUMN_FRAME =
  "flex flex-1 min-w-[17rem] flex-col rounded-lg border border-[color:var(--color-border)]";
const COLUMN_HEADER =
  "flex h-11 items-center gap-2 rounded-t-lg border-b border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3";

// Horizontal column rail — the ONLY x-scrollable region on the page (the
// PageHeader stays fixed; the app shell never grows a horizontal scrollbar —
// app-layout-v2's min-w-0 bounds the flex ancestors). overscroll-x-contain
// stops an edge-swipe (trackpad/iPad) from triggering browser back/forward.
const RAIL_FRAME =
  "flex w-full max-w-full gap-4 overflow-x-auto overscroll-x-contain pb-4";

function BoardSkeleton() {
  return (
    <div className={RAIL_FRAME}>
      {OPPORTUNITY_STAGES.map((stage) => (
        <div key={stage} className={COLUMN_FRAME}>
          <div className={COLUMN_HEADER}>
            <Skeleton className="size-2 rounded-full" />
            <Skeleton className="h-3 w-20" />
          </div>
          <div className="flex flex-col gap-2 p-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-[84px] w-full rounded-md" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * The opportunity pipeline board (ADV-1359). Extracted from the former
 * /opportunities/board page so it renders IN PLACE inside the unified
 * <V2OpportunitiesPage> behind the ViewSwitcher — the shared ListPage owns the
 * header now, so this component is body-only (no PageHeader/breadcrumb).
 */
export function OpportunityBoardView() {
  const navigate = useNavigate();
  // Per-user card chrome (which fields show, density, empty-column visibility)
  // and the shareable within-column ordering drive what the board renders.
  const { properties, density, showEmptyColumns } =
    useOpportunityDisplayStore();
  const { ordering } = useOpportunityViewParams();
  // ADV-1362: the salesperson facet is shared with the table fetch and applied
  // server-side, so the board and table stay in sync on the same selection.
  const filters = useOpportunityFilters();
  // The list endpoint caps `limit` at 100 (PaginationQuerySchema) — requesting
  // more 400s the query and the board would render a misleading empty pipeline.
  // 100 covers the demo; slice 2 pages the board for accounts with more.
  const { data, isLoading, isError } = useOpportunities({
    limit: 100,
    ...filters.params,
  });

  // ADV-1457: the per-column headline "pipeline value" is a DB-computed total,
  // NOT a sum of the (≤100-row) page — client-side reducing proposal_value over
  // one capped page silently undercounts beyond `limit`. Fetch the per-stage
  // aggregate with the SAME salesperson facet the cards use so header and cards
  // agree, then index it by stage (no client-side summation). Its loading/error
  // state joins the board's so a failed summary surfaces (§1.5) instead of
  // silently rendering every column total as $0.
  const {
    data: pipelineSummary,
    isLoading: isSummaryLoading,
    isError: isSummaryError,
  } = useOpportunityPipelineSummary(filters.params);
  const stageTotals = useMemo(() => {
    const totals: Partial<Record<OpportunityStage, number>> = {};
    for (const row of pipelineSummary?.stages ?? []) {
      if ((OPPORTUNITY_STAGES as readonly string[]).includes(row.stage)) {
        totals[row.stage as OpportunityStage] = row.total_proposal_value;
      }
    }
    return totals;
  }, [pipelineSummary?.stages]);

  const opportunities = useMemo(
    () => (data?.opportunities ?? []) as Row[],
    [data?.opportunities],
  );

  const sortField = ordering?.id ?? "lead_date";
  const sortDesc = ordering?.desc ?? true;
  const columnData = useMemo(() => {
    const groups: Record<OpportunityStage, Row[]> = {
      lead: [],
      appointment: [],
      proposal: [],
      closed_won: [],
      closed_lost: [],
    };
    for (const opp of opportunities) {
      groups[asStage(opp.stage)].push(opp);
    }
    const compare = compareRows(sortField, sortDesc);
    for (const stage of OPPORTUNITY_STAGES) groups[stage].sort(compare);
    return groups;
  }, [opportunities, sortField, sortDesc]);

  // "Show empty columns" off → drop stages with no cards (Display popover §3d).
  const visibleStages = showEmptyColumns
    ? OPPORTUNITY_STAGES
    : OPPORTUNITY_STAGES.filter((stage) => columnData[stage].length > 0);

  return (
    <>
      {isLoading || isSummaryLoading ? (
        <BoardSkeleton />
      ) : isError || isSummaryError ? (
        // A failed fetch (cards OR the pipeline-value summary) must not
        // masquerade as an empty/zeroed pipeline (§1.5).
        <p className="py-16 text-center typo-body text-[color:var(--color-text-lo)]">
          Couldn't load the pipeline. Refresh to try again.
        </p>
      ) : (
        <div className={RAIL_FRAME}>
          {visibleStages.map((stage) => {
            const rows = columnData[stage];
            // ADV-1457: DB-computed per-stage total (see stageTotals above) —
            // never a reduce over this page's rows.
            const colTotal = stageTotals[stage] ?? 0;

            return (
              <div key={stage} className={COLUMN_FRAME}>
                {/* Column header: stage dot · label · count pill · full-$ sum.
                    The sum is the prominent element (text-hi) — pipeline value
                    is what the operator scans the header for. */}
                <div className={cn(COLUMN_HEADER, "justify-between")}>
                  <div className="flex min-w-0 items-center gap-2">
                    <span
                      className={cn(
                        "size-2 shrink-0 rounded-full",
                        STAGE_DOT[stage],
                      )}
                      aria-hidden
                    />
                    <span className="truncate typo-label text-[color:var(--color-text-hi)]">
                      {COLUMN_LABELS[stage]}
                    </span>
                    <span className="shrink-0 rounded-full bg-[color:var(--color-bg)] px-1.5 typo-mono tabular-nums text-[color:var(--color-text-md)]">
                      {rows.length}
                    </span>
                  </div>
                  {colTotal > 0 && (
                    <span className="shrink-0 typo-mono font-medium tabular-nums text-[color:var(--color-text-hi)]">
                      {formatCurrency(colTotal)}
                    </span>
                  )}
                </div>

                {/* Cards */}
                <ScrollArea className="max-h-[calc(100vh-220px)] flex-1">
                  <div className="flex flex-col gap-2 p-2">
                    {rows.length === 0 ? (
                      <p className="px-2 py-8 text-center typo-label normal-case tracking-normal text-[color:var(--color-text-lo)]">
                        {COLUMN_EMPTY[stage]}
                      </p>
                    ) : (
                      rows.map((opp) => {
                        const id = opp.id as string;
                        const repName = opp.assigned_rep_name as
                          | string
                          | undefined;
                        // Each card field is gated by its Display-popover chip
                        // (§3c). Territory + Time-in-stage chips are disabled
                        // until their backing data lands (§7 M1/M2, S6), so
                        // those slots are simply absent today.
                        return (
                          <BoardCard
                            key={id}
                            primaryLabel={(opp.name as string) || "Untitled"}
                            amount={
                              properties.amount && opp.proposal_value
                                ? formatCurrency(opp.proposal_value as number)
                                : undefined
                            }
                            secondaryLabel={
                              properties.address
                                ? (opp.address as string | undefined)
                                : undefined
                            }
                            assignee={
                              properties.salesperson && repName
                                ? {
                                    name: repName,
                                    // ADV-1354: face-cropped headshot on the
                                    // card; falls back to a monogram when the
                                    // rep has no photo (RepAvatar derives it).
                                    avatarUrl: opp.assigned_rep_headshot_url as
                                      | string
                                      | null
                                      | undefined,
                                  }
                                : undefined
                            }
                            recency={
                              properties.created
                                ? formatRecency(
                                    opp.lead_date as string | undefined,
                                  )
                                : undefined
                            }
                            density={density}
                            onClick={() => navigate(`/opportunities/${id}`)}
                          />
                        );
                      })
                    )}
                  </div>
                </ScrollArea>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
